import type { Translate } from "@similarweb/i18n-provider";
import {
    formatDate,
    shortMonthDayAndYear,
    TextCell,
    TooltipTextAnchorCell,
    TruncatedTextCell,
    type ColumnsType,
} from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Badge } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { compareItemsByDateDesc, stringSorter } from "app/pages/sales-intelligence/utils/sort";

import type { Playbook } from "../../types/playbooks.types";

import { PlaybooksTableActionsCell } from "./PlaybooksTableActionsCell";

export type PlaybooksColumnHandlers = {
    onEdit: (playbook: Playbook) => void;
    onDelete: (playbook: Playbook) => void;
};

export const getPlaybooksColumns = (translate: Translate, handlers: PlaybooksColumnHandlers): ColumnsType<Playbook> => [
    {
        dataIndex: "name",
        title: translate("si.playbooks.column.playbook"),
        sorter: stringSorter("name"),
        width: 380,
        render: (_value, record) => <TruncatedTextCell>{record.name ?? ""}</TruncatedTextCell>,
    },
    {
        dataIndex: "isSharedWithAccount",
        title: translate("si.playbooks.column.type"),
        width: 110,
        render: (_value, record) => (
            <Box width="fit-content">
                <Badge
                    bgColor={record.isSharedWithAccount ? colorsPalettes.carbon["25"] : colorsPalettes.indigo["100"]}
                    color={record.isSharedWithAccount ? colorsPalettes.carbon["400"] : colorsPalettes.indigo["400"]}
                    type="custom"
                >
                    {translate(record.isSharedWithAccount ? "si.playbooks.type.shared" : "si.playbooks.type.private")}
                </Badge>
            </Box>
        ),
    },
    {
        dataIndex: "cadence",
        title: translate("si.playbooks.column.cadence"),
        render: (_value, record: Playbook) => {
            if (!record.cadence) {
                return <TextCell>-</TextCell>;
            }

            return (
                <TooltipTextAnchorCell
                    href={record.cadence.url}
                    target="_blank"
                    tooltipContent={translate("si.playbooks.column.cadence.view_in_salesloft")}
                    dataAutomation="playbooks-cadence-link"
                >
                    {record.cadence.name}
                </TooltipTextAnchorCell>
            );
        },
    },
    {
        dataIndex: "updatedDate",
        title: translate("si.playbooks.column.updated"),
        width: 140,
        sorter: (a, b) => compareItemsByDateDesc(a.updatedDate, b.updatedDate),
        render: (_value, record) => (
            <TextCell>{formatDate(record.updatedDate, undefined, shortMonthDayAndYear)}</TextCell>
        ),
    },
    {
        key: "actions",
        title: "",
        width: 108,
        render: (_value, record) => (
            <PlaybooksTableActionsCell
                playbook={record}
                onEdit={handlers.onEdit}
                onDelete={handlers.onDelete}
            />
        ),
    },
];
